import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import {
  FormResponse,
  FormResponseRequest,
  ResponseStatus,
  FormTemplate,
  AutomationTrigger
} from '../models/form.model';
import { FormAutomationService } from './form-automation.service';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FormResponseService {
  private apiBase = environment.apiUrl;
  private draftCache = new Map<string, FormResponse>(); // Local cache key: taskId_templateId or projectId_templateId

  constructor(
    private http: HttpClient,
    private automationService: FormAutomationService
  ) {}

  /**
   * Submit form response with automation trigger support
   * @param organizationId - Organization ID
   * @param taskId - Task ID (for task-linked forms)
   * @param templateId - Form template ID
   * @param req - Form response request
   * @param template - Form template (optional, for automation triggers)
   * @returns Observable of form response with automation logs
   */
  submitResponse(
    organizationId: string,
    taskId: string,
    templateId: string,
    req: FormResponseRequest,
    template?: FormTemplate
  ): Observable<FormResponse> {
    //console.log('[FormResponseService] submitResponse:', {
    //   organizationId,
    //   taskId,
    //   templateId,
    //   status: req.status,
    //   timestamp: new Date().toISOString()
    // });

    const url = `${this.apiBase}/organization/${organizationId}/forms/${templateId}/response?taskId=${taskId}`;
    return this.http.post<FormResponse>(url, req, { withCredentials: true }).pipe(
      tap(response => {
        // Cache draft locally
        if (response.status === 'draft') {
          const cacheKey = `task_${taskId}_${templateId}`;
          this.draftCache.set(cacheKey, response);
        }

        // Execute automation triggers asynchronously for submitted responses
        if (response.status === 'submitted' && template?.automationTriggers && template.automationTriggers.length > 0) {
          console.log('[FormResponseService] Executing automation triggers:', template.automationTriggers.length);
          
          // Execute triggers asynchronously (non-blocking)
          this.automationService.executeTriggers(response, template.automationTriggers)
            .then(logs => {
              response.automationLog = logs;
              console.log('[FormResponseService] Automation triggers completed:', logs.length, 'logs');
            })
            .catch(error => {
              console.error('[FormResponseService] Automation triggers failed:', error);
              // Don't fail the response submission on automation errors
            });
        }
      })
    );
  }

  /**
   * Submit project-linked form response with automation trigger support
   * @param organizationId - Organization ID
   * @param projectId - Project ID
   * @param templateId - Form template ID
   * @param req - Form response request
   * @param template - Form template (optional, for automation triggers)
   * @returns Observable of form response with automation logs
   */
  submitProjectResponse(
    organizationId: string,
    projectId: string,
    templateId: string,
    req: FormResponseRequest,
    template?: FormTemplate
  ): Observable<FormResponse> {
    console.log('[FormResponseService] submitProjectResponse:', {
      organizationId,
      projectId,
      templateId,
      status: req.status,
      timestamp: new Date().toISOString()
    });

    const url = `${this.apiBase}/organization/${organizationId}/forms/${templateId}/response`;
    return this.http.post<FormResponse>(url, req, { withCredentials: true }).pipe(
      tap(response => {
        // Cache draft locally
        if (response.status === 'draft') {
          const cacheKey = `project_${projectId}_${templateId}`;
          this.draftCache.set(cacheKey, response);
        }

        // Execute automation triggers asynchronously for submitted responses
        if (response.status === 'submitted' && template?.automationTriggers && template.automationTriggers.length > 0) {
          console.log('[FormResponseService] Executing automation triggers:', template.automationTriggers.length);
          
          // Execute triggers asynchronously (non-blocking)
          this.automationService.executeTriggers(response, template.automationTriggers)
            .then(logs => {
              response.automationLog = logs;
              console.log('[FormResponseService] Automation triggers completed:', logs.length, 'logs');
            })
            .catch(error => {
              console.error('[FormResponseService] Automation triggers failed:', error);
              // Don't fail the response submission on automation errors
            });
        }
      })
    );
  }

  // Legacy method for backwards compatibility
  saveResponse(
    organizationId: string,
    taskId: string,
    templateId: string,
    req: FormResponseRequest
  ): Observable<ResponseStatus> {
    const url = `${this.apiBase}/organization/${organizationId}/forms/${templateId}/response?taskId=${taskId}`;
    return this.http.post<ResponseStatus>(url, req, { withCredentials: true }).pipe(
      tap(status => {
        // Cache draft locally
        if (status === 'draft') {
          const cacheKey = `task_${taskId}_${templateId}`;
          const response: FormResponse = {
            organizationId,
            projectId: req.projectId || '',
            taskId,
            templateId,
            templateVersion: 1,
            status: 'draft',
            answers: req.answers,
            files: req.files || [],
            images: req.images || [],
            updatedAt: new Date().toISOString()
          };
          this.draftCache.set(cacheKey, response);
        }
      })
    );
  }

  // Legacy method for backwards compatibility
  saveProjectResponse(
    organizationId: string,
    projectId: string,
    templateId: string,
    req: FormResponseRequest
  ): Observable<ResponseStatus> {
    const url = `${this.apiBase}/organization/${organizationId}/forms/${templateId}/response`;
    return this.http.post<ResponseStatus>(url, req, { withCredentials: true }).pipe(
      tap(status => {
        // Cache draft locally
        if (status === 'draft') {
          const cacheKey = `project_${projectId}_${templateId}`;
          const response: FormResponse = {
            organizationId,
            projectId,
            templateId,
            templateVersion: 1,
            status: 'draft',
            answers: req.answers,
            files: req.files || [],
            images: req.images || [],
            updatedAt: new Date().toISOString()
          };
          this.draftCache.set(cacheKey, response);
        }
      })
    );
  }

  // Fetch form responses for a task
  getTaskResponses(
    templateId: string,
    organizationId: string,
    projectId: string,
    taskId: string
  ): Observable<FormResponse[]> {
    return this.http.get<FormResponse[]>(
      `${this.apiBase}/organization/${organizationId}/forms/${templateId}/response?taskId=${taskId}`,
      { withCredentials: true }
    );
  }

  // Fetch form responses for a project
  getProjectResponses(
    organizationId: string,
    projectId: string,
    templateId: string
  ): Observable<FormResponse[]> {
    return this.http.get<FormResponse[]>(
      `${this.apiBase}/organization/${organizationId}/forms/response?projectId=${projectId}&templateId=${templateId}`,
      { withCredentials: true }
    );
  }

  // Get cached draft if available
  getCachedDraft(cacheKey: string): FormResponse | undefined {
    return this.draftCache.get(cacheKey);
  }

  // Clear cache for a specific key or all
  clearCache(cacheKey?: string): void {
    if (cacheKey) {
      this.draftCache.delete(cacheKey);
    } else {
      this.draftCache.clear();
    }
  }

  /**
   * Reset a submitted form response back to draft state
   * @param organizationId - Organization ID
   * @param taskId - Task ID (optional, for task-linked forms)
   * @param projectId - Project ID (optional, for project-linked forms)
   * @param templateId - Form template ID
   * @returns Observable of updated form response
   */
  resetToDraft(
    organizationId: string,
    templateId: string,
    taskId?: string,
    projectId?: string
  ): Observable<FormResponse> {
    console.log('[FormResponseService] resetToDraft:', {
      organizationId,
      taskId,
      projectId,
      templateId,
      timestamp: new Date().toISOString()
    });

    const queryParam = taskId ? `?taskId=${taskId}` : projectId ? `?projectId=${projectId}` : '';
    const url = `${this.apiBase}/organization/${organizationId}/forms/${templateId}/response/state${queryParam}`;
    const body = { status: 'draft' };
    
    return this.http.patch<FormResponse>(url, body, { withCredentials: true }).pipe(
      tap(response => {
        console.log('[FormResponseService] Form reset to draft:', response);
      })
    );
  }
}
